import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../../components/marketing/PublicLayout";
import { useMarketingLang } from "../../lib/useMarketingLang";
import { Search, ChevronDown, ArrowRight } from "lucide-react";

export default function MarketingFaq() {
  const { t } = useMarketingLang();
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return t.faq.categories;
    return t.faq.categories
      .map((c) => ({
        ...c,
        items: c.items.filter(
          (it) =>
            it.q.toLowerCase().includes(q) ||
            it.a.toLowerCase().includes(q),
        ),
      }))
      .filter((c) => c.items.length > 0);
  }, [q, t]);

  return (
    <PublicLayout>
      <section className="bg-[#0F1A2B] text-white py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.42em] text-[#ECC974] mb-3">{t.faq.eyebrow}</div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-3 leading-tight">{t.faq.title}</h1>
          <p className="text-white/75 mb-2">{t.faq.subtitle}</p>
          <p className="text-white/60 text-sm">{t.faq.intro}</p>

          <div className="mt-8 relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#BD984D]" />
            <input
              data-testid="faq-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.faq.searchPlaceholder}
              className="w-full rounded-full bg-white/10 border border-white/20 pl-12 pr-4 py-3.5 text-white placeholder:text-white/50 focus:outline-none focus:border-[#BD984D]"
            />
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 lg:px-8 py-14">
        {filtered.length === 0 && (
          <div className="text-center text-[#0F1A2B]/60 py-10">
            Ingen treff. Ta kontakt så hjelper vi deg direkte.
          </div>
        )}
        {filtered.map((cat, ci) => (
          <div key={ci} className="mb-10" data-testid={`faq-cat-${ci}`}>
            <div className="border-b border-[#A57F39]/30 pb-2 mb-4">
              <h2 className="text-sm uppercase tracking-[0.28em] font-bold text-[#A57F39]">
                {cat.name}
              </h2>
            </div>
            <div className="space-y-3">
              {cat.items.map((it, i) => {
                const id = `${ci}-${i}`;
                const isOpen = openId === id;
                return (
                  <div
                    key={id}
                    className={`rounded-xl border transition ${
                      isOpen
                        ? "border-[#A57F39]/50 bg-[#A57F39]/5"
                        : "border-[#A57F39]/15 bg-white hover:border-[#A57F39]/30"
                    }`}
                  >
                    <button
                      type="button"
                      data-testid={`faq-q-${id}`}
                      onClick={() => setOpenId(isOpen ? null : id)}
                      className="w-full flex items-center justify-between gap-4 text-left px-5 py-4"
                    >
                      <span className="text-[#0F1A2B] font-semibold">{it.q}</span>
                      <ChevronDown
                        className={`h-5 w-5 text-[#A57F39] shrink-0 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div
                        data-testid={`faq-a-${id}`}
                        className="px-5 pb-5 text-[#0F1A2B]/80 text-sm leading-relaxed whitespace-pre-line"
                      >
                        {it.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      <section className="bg-[#F8F7F2] py-12 text-center">
        <div className="max-w-2xl mx-auto px-4">
          <p className="text-lg text-[#0F1A2B] mb-4">{t.faq.contactCta}</p>
          <Link
            to="/kontakt"
            data-testid="faq-contact-link"
            className="inline-flex items-center gap-2 rounded-full bg-[#A57F39] hover:bg-[#BD984D] transition text-white uppercase tracking-widest text-xs font-bold px-8 py-3.5"
          >
            {t.nav.contact} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
